/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Checkbox } from 'primereact/checkbox';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSearchParams } from 'react-router-dom';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { evalRunService } from '../../services/eval-run.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { EvalRun, EvalRunStatus } from '../../types';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';
import type { Tone } from '../../components/common/ui-kit';

/** Same tone for the same status as the run detail and the RAG screens. */
const statusTone = (status?: EvalRunStatus): Tone => {
  switch (status) {
    case 'completed': return 'success';
    case 'failed': return 'danger';
    case 'partial': return 'warn';
    case 'cancelled': return 'neutral';
    default: return 'info';
  }
};

/** 40×40 keeps the row action above the desktop hit-area floor. */
const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';

const EvalRunList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToView } = useSecureNavigation({
    entity: 'eval-run',
    basePath: '/eval-runs',
  });
  const toast = useRef<Toast>(null);

  // Optional deep-link filters: /eval-runs?promptRef=... or ?suiteRef=...
  const [searchParams] = useSearchParams();
  const promptRef = searchParams.get('promptRef') || undefined;
  const suiteRefParam = searchParams.get('suiteRef') || undefined;

  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [hideReruns, setHideReruns] = useState(true);  // hide single-case re-runs by default
  const [showCore, setShowCore] = useState(false);

  // Server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchRuns = useCallback(
    async (page = 1, size = pageSize) => {
      try {
        setLoading(true);
        const response = await evalRunService.listRuns({
          page, pageSize: size, promptRef, suiteRef: suiteRefParam, hideSingleCase: hideReruns,
          include_global: showCore,
        });
        // Surface the meaningful results first: COMPLETED runs with a pass rate > 0
        // go to the top, so partial/failed/0% runs (which otherwise dominate the
        // view) sink below. Stable sort keeps the server order (newest first) within
        // each group.
        const data = response.data || [];
        const rank = (r: EvalRun) => {
          const pr = r.summary?.passRate;
          return r.status === 'completed' && typeof pr === 'number' && pr > 0 ? 0 : 1;
        };
        setRuns([...data].sort((a, b) => rank(a) - rank(b)));
        const total =
          response.pagination?.totalItems ??
          response.pagination?.total ??
          (response.data || []).length;
        setTotalRecords(total);
      } catch (error) {
        console.error('Failed to fetch eval runs:', error);
        showError(extractErrorMessage(error, 'Failed to load eval runs'));
      } finally {
        setLoading(false);
      }
    },
    [pageSize, showError, promptRef, suiteRefParam, hideReruns, showCore]
  );

  useEffect(() => {
    setFirst(0);
    fetchRuns(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptRef, suiteRefParam, hideReruns, showCore]);

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchRuns(newPage, event.rows);
  };

  const handleView = (row: EvalRun) => {
    const ref = row._id || row.evalRunId;
    if (ref) navigateToView(ref);
  };

  const columns: SaasColumn<EvalRun>[] = useMemo(
    () => [
      {
        field: 'evalRunId',
        header: 'Run ID',
        hideable: false,
        body: (r) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.evalRunId || r._id || '—'}</span>,
      },
      { field: 'suiteCode', header: 'Suite', body: (r) => r.suiteCode || r.suiteRef || '-' },
      {
        field: 'status',
        header: 'Status',
        body: (r) => (
          <StatusChip tone={statusTone(r.status)}>{r.status || '—'}</StatusChip>
        ),
      },
      {
        field: 'passRate',
        header: 'Pass Rate',
        align: 'right',
        body: (r) => {
          const rate = r.summary?.passRate;
          if (rate === null || rate === undefined) return <span className="text-ink-muted">—</span>;
          return (
            <span className={`ui-ink tabular-nums font-medium ${rate >= 1 ? 'ui-ink--good' : rate === 0 ? 'ui-ink--bad' : ''}`}>
              {(rate * 100).toFixed(1)}%
            </span>
          );
        },
      },
      {
        field: 'passed',
        header: 'Passed / Total',
        align: 'right',
        body: (r) => <span className="tabular-nums">{r.summary?.passed ?? '—'} / {r.summary?.total ?? '—'}</span>,
      },
      {
        field: 'totalCost',
        header: 'Total Cost',
        align: 'right',
        body: (r) => {
          const c = r.summary?.totalCost;
          if (c === null || c === undefined) return <span className="text-ink-muted">—</span>;
          const cur = r.summary?.currency ? `${r.summary.currency} ` : '$';
          return <span className="tabular-nums">{cur}{c.toFixed(c < 1 ? 4 : 2)}</span>;
        },
      },
      {
        field: 'runDateTime',
        header: 'Run At',
        body: (r) => <span className="tabular-nums">{r.runDateTime ? formatDate(r.runDateTime) : r.createdAt ? formatDate(r.createdAt) : '—'}</span>,
      },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex gap-1 justify-center">
            <button
              type="button"
              className={rowAction}
              onClick={() => handleView(r)}
              data-testid="EvalRunList.Action.View"
              data-pr-tooltip="View"
              aria-label="View run"
            >
              <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <PermissionGuard jobName={JOB_NAMES.EVAL_RUNS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-EvalRun-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-bolt"
          title="Eval Runs"
          subtitle="History of eval executions and their results"
          testId="EvalRun.Header"
        />

        <div
          className={
            embedded
              ? ''
              : 'ui-panel overflow-hidden'
          }
        >
          <SaasDataTable
            tableId="eval-run"
            columns={columns}
            data={runs}
            loading={loading}
            dataKey="_id"
            emptyMessage="No eval runs found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onSort={() => {}}
            onFilter={() => {}}
            testId="EvalRunList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <Checkbox
                    inputId="hideReruns"
                    checked={hideReruns}
                    onChange={(e) => setHideReruns(!!e.checked)}
                    data-testid="EvalRunList.HideReruns"
                  />
                  Hide single-case re-runs
                </label>
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="EvalRunList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchRuns(Math.floor(first / pageSize) + 1, pageSize)}
                  loading={loading}
                  testId="EvalRunList.Button.Refresh"
                />
              </>
            }
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default EvalRunList;
